import { isBefore, isEqual } from 'date-fns';
import { Row } from '@tanstack/react-table';
import { PositionView } from '@paradigm/features/src/positions/entities/positions';
import { convertToBigNumber } from '@paradigm/utils/formatDecimal';

export function sortUnrealizedPnl(
  rowA: Row<PositionView>,
  rowB: Row<PositionView>,
) {
  if (rowA.getCanExpand() || rowB.getCanExpand()) {
    const unrealizedPnlA: PositionView['unrealizedPnl'] =
      rowA.getValue('unrealizedPnl');
    const unrealizedPnlB: PositionView['unrealizedPnl'] =
      rowB.getValue('unrealizedPnl');

    return unrealizedPnlA.value.comparedTo(unrealizedPnlB.value);
  }
  return rowA.original.unrealizedPnl.value.comparedTo(
    rowB.original.unrealizedPnl.value,
  );
}

export function sortQuantity(rowA: Row<PositionView>, rowB: Row<PositionView>) {
  if (isExpandable(rowA, rowB)) {
    const quantityA: string = rowA.getValue('quantity');
    const quantityB: string = rowB.getValue('quantity');

    const numberA = convertToBigNumber(quantityA.replaceAll(',', ''));
    const numberB = convertToBigNumber(quantityB.replaceAll(',', ''));

    return numberA.comparedTo(numberB);
  }

  return rowA.original.quantity.value.comparedTo(rowB.original.quantity.value);
}

export function sortExpiry(rowA: Row<PositionView>, rowB: Row<PositionView>) {
  if (isExpandable(rowA, rowB)) {
    const expiryAtA: PositionView['expiresAt'] = rowA.getValue('expires_at');
    const expiryAtB: PositionView['expiresAt'] = rowB.getValue('expires_at');

    const dateA = expiryAtA.value ?? new Date(-1);
    const dateB = expiryAtB.value ?? new Date(-1);

    if (isEqual(dateA, dateB)) return 0;
    return isBefore(dateA, dateB) ? -1 : 1;
  }

  const dateA = rowA.original.expiresAt.value ?? new Date(-1);
  const dateB = rowB.original.expiresAt.value ?? new Date(-1);

  if (isEqual(dateA, dateB)) return 0;
  return isBefore(dateA, dateB) ? -1 : 1;
}

export function sortRowStringsByKey(key: keyof PositionView) {
  return (rowA: Row<PositionView>, rowB: Row<PositionView>): 0 | 1 | -1 => {
    if (rowA.getCanExpand() || rowB.getCanExpand()) {
      const valueA = rowA.getValue<string>(key);
      const valueB = rowB.getValue<string>(key);
      return rawCompare(valueA, valueB);
    }

    const valueA = rowA.original[key];
    const valueB = rowB.original[key];

    if (typeof valueA === 'string' && typeof valueB === 'string')
      return rawCompare(valueA, valueB);

    console.error(`sortRowStringsByKey: ${key} is not a string`);
    return 0;
  };
}

const isExpandable = (rowA: Row<PositionView>, rowB: Row<PositionView>) =>
  rowA.getCanExpand() || rowB.getCanExpand();

const rawCompare = (a: string, b: string) => {
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};
